import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type { SchoolSummary } from '@ucp/web/lib';
import { isOperationalSchoolId } from '../lib/identity/schoolIds';
import { useSchools } from '../hooks/useSchools';
import { useSchoolContext } from './SchoolContextProvider';

/**
 * School directory context (PRO-0030 name display). Fetches `GET /schools` once
 * per active-school change and exposes human-readable names for the header
 * switcher and Home meta line. Tenancy choice stays in `SchoolContextProvider`.
 */

export type SchoolDirectoryStatus = 'loading' | 'ready' | 'error';

export interface SchoolDirectoryValue {
  readonly schools: readonly SchoolSummary[];
  readonly activeSchoolName: string | null;
  readonly status: SchoolDirectoryStatus;
  readonly errorMessage: string | null;
}

const SchoolDirectoryContext = createContext<SchoolDirectoryValue | undefined>(undefined);

const visuallyHiddenStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function SchoolDirectoryProvider({ children }: { children: ReactNode }) {
  const { activeSchoolId } = useSchoolContext();
  const schoolsState = useSchools(activeSchoolId);
  const prevNameRef = useRef<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const value = useMemo((): SchoolDirectoryValue => {
    if (schoolsState.status === 'loading') {
      return { schools: [], activeSchoolName: null, status: 'loading', errorMessage: null };
    }
    if (schoolsState.status === 'error') {
      return {
        schools: [],
        activeSchoolName: null,
        status: 'error',
        errorMessage: schoolsState.message,
      };
    }

    const { data } = schoolsState;
    const operationalSchools = data.schools
      .filter((school) => isOperationalSchoolId(school.schoolId))
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    const activeSchoolName =
      operationalSchools.find((s) => s.schoolId === data.activeSchoolId)?.name ??
      operationalSchools[0]?.name ??
      (data.activeSchoolId && isOperationalSchoolId(data.activeSchoolId) ? data.activeSchoolId : null);

    return {
      schools: operationalSchools,
      activeSchoolName: activeSchoolName ?? null,
      status: 'ready',
      errorMessage: null,
    };
  }, [schoolsState]);

  useEffect(() => {
    if (value.status !== 'ready' || !value.activeSchoolName) {
      return;
    }
    if (prevNameRef.current !== null && prevNameRef.current !== value.activeSchoolName) {
      setAnnouncement(`Active school changed to ${value.activeSchoolName}`);
    }
    prevNameRef.current = value.activeSchoolName;
  }, [value.status, value.activeSchoolName]);

  return (
    <SchoolDirectoryContext.Provider value={value}>
      {children}
      {announcement ? (
        <div role="status" aria-live="polite" style={visuallyHiddenStyle}>
          {announcement}
        </div>
      ) : null}
    </SchoolDirectoryContext.Provider>
  );
}

export function useSchoolDirectory(): SchoolDirectoryValue {
  const context = useContext(SchoolDirectoryContext);
  if (!context) {
    throw new Error('useSchoolDirectory must be used within a SchoolDirectoryProvider');
  }
  return context;
}
